import { Quaternion, Vector3 } from 'three'
import { aimQuaternion, gripById, gripPosition, type GripPreset } from './gripPresets'
import type { InputSource, PilotInput } from './types'

/** Pilot state fed back to the script, so a test can model a closed-loop pilot. */
export interface ScriptState {
  /** Body orientation, world frame. */
  bodyQuaternion: Quaternion
  tiltDegrees: number
  /** Feet above the ground, metres. */
  altitude: number
  /** CoM position and velocity, world frame. */
  position: Vector3
  velocity: Vector3
  grounded: boolean
}

/** What a scripted test commands at a given instant. */
export interface ScriptFrame {
  /** 0..1 per rocket. */
  throttle: [number, number]
  /** Radians away from nozzles-down, per rocket: [pitch, roll]. */
  aim?: [[number, number], [number, number]]
  /**
   * `body` (default) holds the nozzles fixed relative to the body — the thrust
   * vector then rotates with the body, so attitude has no effect on torque at
   * all. That is the pendulum rocket fallacy (§1) and it means grip height is
   * invisible in body mode.
   *
   * `world` holds the nozzles pointed down in *world* space, i.e. models a
   * pilot who is actively doing their job. Grip height only becomes a
   * restoring-vs-diverging torque under this mode, which is exactly the
   * design's claim: the human is the stabiliser.
   */
  aimMode?: 'body' | 'world'
  /** Override the grip for this instant (defaults to the scenario's grip). */
  grip?: GripPreset | string
  /** Head XZ offset from neutral, metres — drives the dynamic-CoM lean input. */
  lean?: [number, number]
  /** Per-hand tracking state; false decays that rocket's thrust (§2). */
  tracked?: [boolean, boolean]
}

export type ScriptFn = (t: number, state: ScriptState) => ScriptFrame

/**
 * Deterministic input with no input device at all — layer 1 of the desktop sim
 * (DESIGN.md §4). This is where most physics iteration happens: same script,
 * different tuning, compare the traces.
 */
export class ScriptSource implements InputSource {
  readonly name = 'script'
  private t = 0

  /** Mutated by the harness each step before `sample`. */
  readonly state: ScriptState = {
    bodyQuaternion: new Quaternion(),
    tiltDegrees: 0,
    altitude: 0,
    position: new Vector3(),
    velocity: new Vector3(),
    grounded: false,
  }

  constructor(
    private readonly script: ScriptFn,
    private readonly defaultGrip: GripPreset,
    private readonly standingHeight: number,
  ) {}

  sample(out: PilotInput, dt: number): void {
    const frame = this.script(this.t, this.state)
    this.t += dt
    const worldAim = frame.aimMode === 'world'
    if (worldAim) _invBody.copy(this.state.bodyQuaternion).invert()

    const grip =
      typeof frame.grip === 'string' ? gripById(frame.grip) : (frame.grip ?? this.defaultGrip)
    const lean = frame.lean ?? [0, 0]

    out.head.position.set(lean[0], this.standingHeight, lean[1])
    out.head.quaternion.identity()
    out.head.tracked = true

    for (let i = 0; i < 2; i++) {
      const hand = out.hands[i]
      gripPosition(i === 0 ? grip.left : grip.right, this.standingHeight, _slot)
      hand.position.copy(_slot)
      const aim = frame.aim?.[i] ?? [0, 0]
      aimQuaternion(aim[0], aim[1], hand.quaternion)
      // The pilot's hands live in tracking space, which the body carries
      // around; to hold a world-space aim, undo the body rotation first.
      if (worldAim) hand.quaternion.premultiply(_invBody)
      hand.throttle = frame.throttle[i]
      hand.tracked = frame.tracked?.[i] ?? true
    }
  }

  get time(): number {
    return this.t
  }
}

const _slot = new Vector3()
const _invBody = new Quaternion()
